import React, { forwardRef } from 'react';

import { cn } from '../../lib/css/className/className';
import { useStack } from '../../hooks/useStack';
import { useLayoutStyle } from '../../lib/layoutStyle/layoutStyle';

import { StackProps, HVStackProps } from './types';

const cls = cn('Stack');

/**
 * Внутренний хук, инкапсулирующий рендер Stack-элемента.
 *
 * Используется напрямую из `Stack` / `HStack` / `VStack`, чтобы не вкладывать
 * один компонент в другой (не увеличивать глубину дерева) и при этом не
 * нарушать Rules of Hooks — у каждого из этих компонентов получается свой
 * собственный fiber, на котором честно вызываются хуки.
 */
function useStackElement(
    {
        className,
        children,

        type = 'horizontal',
        scrollAxis,

        flexWrap,
        justifyContent,
        alignItems,

        gap,

        as: ElementTag = 'div',
        inline = false,

        width,
        minWidth,
        maxWidth,
        height,
        minHeight,
        maxHeight,
        flex,

        ...props
    }: StackProps,
    ref: React.ForwardedRef<HTMLElement>,
) {
    const stackClasses = useStack({
        type,
        scrollAxis,
        gap,
        flexWrap,
        justifyContent,
        alignItems,
        inline,
    });

    const { style: layoutStyle, className: layoutClassName } = useLayoutStyle({
        width,
        minWidth,
        maxWidth,
        height,
        minHeight,
        maxHeight,
        flex,
    });

    return (
        <ElementTag
            ref={ref}
            data-testid={props['data-testid']}
            {...props}
            className={cls({}, [stackClasses, layoutClassName, className])}
            style={layoutStyle}
        >
            {children}
        </ElementTag>
    );
}

/**
 * Компонент-контейнер для позиционирования дочерних компонентов.
 * Используется для создания кнопок, полей ввода, форм, списков и т.д.
 */
export const Stack = forwardRef(function Stack(props: StackProps, ref: React.ForwardedRef<HTMLElement>) {
    return useStackElement(props, ref);
});

Stack.displayName = 'Stack';

/**
 * Компонент-контейнер для горизонтального позиционирования дочерних компонентов (в строку).
 * Используется для создания кнопок, полей ввода и т.д.
 */
export const HStack = forwardRef(function HStack(
    { scrollable, ...otherProps }: HVStackProps,
    ref: React.ForwardedRef<HTMLElement>,
) {
    const scrollAxis = scrollable ? 'x' : undefined;
    return useStackElement({ scrollAxis, ...otherProps }, ref);
});

HStack.displayName = 'HStack';

/**
 * Компонент-контейнер для вертикального позиционирования дочерних компонентов (в столбец).
 * Используется для создания форм, списков и т.д.
 */
export const VStack = forwardRef(function VStack(
    { scrollable, ...otherProps }: HVStackProps,
    ref: React.ForwardedRef<HTMLElement>,
) {
    const scrollAxis = scrollable ? 'y' : undefined;
    return useStackElement({ type: 'vertical', scrollAxis, ...otherProps }, ref);
});

VStack.displayName = 'VStack';
